'use client';

import {
	useInfiniteQuery,
	UseInfiniteQueryOptions,
	UseInfiniteQueryResult,
	InfiniteData,
} from '@tanstack/react-query';
import { ServerActionResult } from '@/shared/types/server-action.type';
import { useGlobalDialogStore } from '@/shared/stores/useGlobalDialogStore';
import { errorRegistry } from '@/core/error';
import { withAuth } from '../utils/withAuth';

// Custom error class to carry the error code
class SafeInfiniteQueryError extends Error {
	constructor(
		message: string,
		public readonly code: string
	) {
		super(message);
		this.name = 'SafeInfiniteQueryError';
	}
}

function isGlobalError(error: Error | null): boolean {
	return (
		error instanceof SafeInfiniteQueryError &&
		errorRegistry[error.code] !== undefined
	);
}

interface UseSafeInfiniteQueryOptions<TData, TPageParam> extends Omit<
	UseInfiniteQueryOptions<
		TData,
		Error,
		InfiniteData<TData>,
		unknown[],
		TPageParam
	>,
	'queryFn'
> {
	action: (pageParam: TPageParam) => Promise<ServerActionResult<TData>>;
}

export function useSafeInfiniteQuery<TData, TPageParam = number>(
	options: UseSafeInfiniteQueryOptions<TData, TPageParam>
): UseInfiniteQueryResult<InfiniteData<TData>, Error> {
	const { action, ...queryOptions } = options;
	const openDialog = useGlobalDialogStore((state) => state.openDialog);

	const query = useInfiniteQuery({
		...queryOptions,
		queryFn: async ({ pageParam }) => {
			const result = await withAuth(action)(pageParam as TPageParam);

			if (!result.success) {
				const errorCode = result.error?.code ?? 'GENERIC_ERROR';
				const errorMessage =
					result.error?.message ??
					'Something went wrong! Please try again.';

				const dialogConfig = errorRegistry[errorCode];
				if (dialogConfig) {
					openDialog(dialogConfig);
				}
				throw new SafeInfiniteQueryError(errorMessage, errorCode);
			}

			return result.data as TData;
		},
	});

	// Filter out global errors - component cannot see them
	const globalError = isGlobalError(query.error);
	return {
		...query,
		error: globalError ? null : query.error,
		isError: globalError ? false : query.isError,
	} as UseInfiniteQueryResult<InfiniteData<TData>, Error>;
}
